import { FlyerType } from '@/config/constants';
import { GET_FACETS } from '@/graphql/queries';
import {
  flyerboardActiveFiltersVar,
  flyerboardAllFiltersVar,
  flyerboardFlyersAmountVar
} from '@/graphql/state';
import { Facets, GetFacetsResponse } from '@/graphql/types';
import { useAnalytics } from '@/hooks/useAnalytics';
import { communityCategories } from '@/mock/communityCategoriesMock';
import { schoolCategories } from '@/mock/schoolCategoriesMock';
import { ages, gradesString } from '@/pages/AccountInformation/AccountInformation.constants';
import { GenericTrackTypes } from '@/types/genericTrackTypes';
import { isMobile } from '@/utils/isMobile';
import { useQuery, useReactiveVar } from '@apollo/client';
import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { DrawerFilter } from './DrawerFilter';
import { ModalFilter } from './ModalFilter';
import { EventDetails, TrackingEvents, TrackingStage } from '@/config/tracking';

interface FiltersProps {
  trackId: string;
  flyerType: string;
  showFilter: boolean;
  setShowFilter: (showFilter: boolean) => void;
  trackingEventFrom?: string;
}

type OptionType = {
  value: string;
  description: string;
  amount: number;
  disabled?: boolean;
};

export const INITIAL_FILTERS = {
  grades_age: [],
  types: [],
  format: [],
  audiences: [],
  categories: [],
  grades: [],
  saved: [],
  type: [],
  sort: [],
  when: [],
};

export const FlyerboardFilters: React.FC<FiltersProps> = ({
  trackId,
  flyerType,
  showFilter,
  setShowFilter,
  trackingEventFrom,
}) => {
  const { search } = useLocation();
  const isMobileDevice = isMobile();
  const searchParams = new URLSearchParams(search);
  const categoryId = searchParams.get('categoryId');
  const flyerboardFlyersAmount = useReactiveVar(flyerboardFlyersAmountVar);
  const flyerboardActiveFilters = useReactiveVar(flyerboardActiveFiltersVar);
  const flyerboardAllFilters = useReactiveVar(flyerboardAllFiltersVar);

  const [localFilters, setLocalFilters] = useState<{
    grades_age: string[],
    types: string[],
  }>({
    grades_age: [],
    types: [],
  });

  const queryInput = useMemo(() => {
    const filters = { ...flyerboardAllFilters }
    delete filters.categories
    delete filters.gradesOrAges

    const { types, grades_age } = localFilters
    if (categoryId) {
      filters.categories = [categoryId]
    } else if (types.length > 0) {
      filters.categories = [...types]
    }
    if (grades_age.length > 0) {
      filters.gradesOrAges = [...grades_age]
    }
    return {
      filters
    }
  }, [flyerboardAllFilters, localFilters])

  const { data, previousData } = useQuery<GetFacetsResponse>(GET_FACETS, {
    variables: {
      input: queryInput
    },
    skip: flyerboardAllFilters === null
  })
  const facets = data?.getFacets.facets ?? previousData?.getFacets.facets;
  const amount = data?.getFacets.pagination.total ?? previousData?.getFacets.pagination.total ?? 0;

  const filterSections = useMemo<{
    grades_age?: OptionType[],
    types: OptionType[] }>(() => {
    if (!facets) return { grades_age: [], types: [] };
    // const grades_age = flyerType === FlyerType.school ? getGrades(facets) : getAges(facets);
    const categoryOptions = categoryId ? [] : getCategories(flyerType, facets);
    return {
      // grades_age,
      types: categoryOptions,
    };
  }, [flyerType, facets])

  const { track, trackEvent } = useAnalytics();
  const title = `${flyerType} Filters`;

  const persistFilters = () => {
    const currentDate = new Date().toString();
    localStorage.setItem('filtersUpdateAt', currentDate);
    localStorage.setItem('flyerboardFilters', JSON.stringify(localFilters));
    localStorage.setItem('activeFlyerboard', window.location.href);
  };

  const handleFilterToggle = (filterName: string, value: string, description: string) => () => {
    setLocalFilters(localFilters => {
      const index = localFilters[filterName].indexOf(value);
      track({
        action: GenericTrackTypes.CHECKBOX_SELECTED,
        category: `${trackId}:filter:${filterName}:${index === -1 ? 'select' : 'unselect'
          }`,
        label: description,
        value: Number(value),
      });

      const newFilters = { ...localFilters };
      if (index === -1) {
        newFilters[filterName].push(value);
      } else {
        newFilters[filterName].splice(index, 1);
      }
      newFilters[filterName].sort();
      return newFilters
    });
  };

  const clearLocalFilters = () => {
    setLocalFilters({
      grades_age: [],
      types: [],
    });
  };

  const onClearFilters = () => {
    track({
      action: GenericTrackTypes.BUTTON_PRESSED,
      category: `${trackId}:clear_filters`,
      label: 'Clear All',
    });
    clearLocalFilters();
  };

  const onToggleDrawer = (drawerState: boolean) => {
    setShowFilter(drawerState);
  };

  useEffect(() => {
    if (showFilter) {
      setLocalFilters({ ...flyerboardActiveFilters })
    }
  }, [showFilter])

  const onShowResults = () => {
    track({
      action: GenericTrackTypes.BUTTON_PRESSED,
      category: `${trackId}:show_results`,
      label: 'Show Results',
    });
    persistFilters();
    flyerboardActiveFiltersVar({ ...localFilters });

    const gradesAgeEvent = flyerType === FlyerType.school ? 'grades' : 'ages';
    const trackingData = {
      stage: TrackingStage.discovery,
      event: TrackingEvents.filterFlyerSet,
      event_detail_from: trackingEventFrom,
      stage_from: TrackingStage.discovery,
      event_from: TrackingEvents.viewFlyerSet,
    }

    if (localFilters.grades_age.length > 0) {
      trackEvent({
        ...trackingData,
        event_detail: EventDetails.filterFlyerSet(gradesAgeEvent),
      })
    }
    if (localFilters.types.length > 0) {
      trackEvent({
        ...trackingData,
        event_detail: EventDetails.filterFlyerSet('types'),
      })
    }

    setShowFilter(false);
  };

  const disableClearAllButton = useMemo(() => {
    const { types, grades_age } = localFilters;
    return types.length === 0 && grades_age.length === 0;
  }, [localFilters]);

  const viewResultsLabel = useMemo(() => {
    let flyersAmount = ""
    if (amount > 0) {
      flyersAmount = amount.toString();
    }
    else if (flyerboardFlyersAmount > 0) {
      flyersAmount = flyerboardFlyersAmount.toString();
    }

    return `View${flyersAmount ? ` ${flyersAmount} ` : ' '} results`;
  }, [amount, flyerboardFlyersAmount]);

  const props = {
    isOpen: showFilter,
    title,
    viewResultsLabel,
    onClearFilters,
    onShowResults,
    onToggle: onToggleDrawer,
    filterSections,
    localFilters,
    disableClearAllButton,
    flyerType,
    handleFilterToggle,
  }

  return isMobileDevice ? (
    <DrawerFilter {...props} />
  ) : (
    <ModalFilter {...props} />
  );

};

function getGrades(filterFacets: Facets) {
  const gradeOptions: OptionType[] = [];
  gradesString.forEach((grade) => {
    const facet = filterFacets.gradesOrAges.find(g => g.key === grade.id);
    let amount: number = 0;
    if (facet) {
      amount = facet.count;
    }
    gradeOptions.push({
      description: grade.description,
      value: grade.id,
      amount,
      disabled: amount === 0
    });
  });

  return gradeOptions;
}

function getAges(filterFacets: Facets) {
  const agesOptions: OptionType[] = [];
  ages.forEach(age => {
    const facet = filterFacets.gradesOrAges.find(g => g.key === age.id);
    let amount: number = 0;
    if (facet) {
      amount = facet.count;
    }
    agesOptions.push({
      description: age.description,
      value: age.id,
      amount,
      disabled: amount === 0
    });
  });

  return agesOptions;
}

function getCategories(flyerType: string, filterFacets: Facets) {
  const categories = flyerType === FlyerType.school ? schoolCategories : communityCategories;
  const categoryOptions: OptionType[] = [];
  categories.forEach(category => {
    const facet = filterFacets.categories.find(g => g.key === category.id);
    let amount: number = 0;
    if (facet) {
      amount = facet.count;
    }
    categoryOptions.push({
      description: category.title,
      value: category.id,
      amount,
      disabled: amount === 0
    });
  });

  return categoryOptions;
}
